import { clientStorage } from "@/lib/clientStorage";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import React, { useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { getSafeBackUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Send, FileText, Printer, Mail, ChevronDown, Download, ArrowLeft, Paperclip, Loader2, FolderOpen, Share2, Copy, X, Save } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { generateVLAPdf } from "../lib/vlaPdfGeneratorV2";
import { createTelephoneDeclarationPdf, printTelephoneDeclaration } from "@/lib/telephoneDeclarationDocument";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { actionstepFolderFor } from "@/lib/actionstepFolders";
import { portalDocumentName } from "@/lib/portalPdf";
import { saveAs } from 'file-saver';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const savePDFFile = async (blob, filename) => {
  if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
    try {
      const w = /** @type {any} */ (window);
      const handle = await w.showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: 'PDF Document',
          accept: {
            'application/pdf': ['.pdf'],
          },
        }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('User cancelled save picker');
        return;
      }
      console.warn('showSaveFilePicker failed, falling back to file-saver:', err);
    }
  }
  saveAs(blob, filename);
};

import FormProgress, { STEPS } from '../components/form/FormProgress';
import Step1PersonalDetails from '../components/form/Step1PersonalDetails';
import Step2LanguageHealth from '../components/form/Step2LanguageHealth';
import Step3Employment from '../components/form/Step3Employment';
import Step4CustodyRelationship from '../components/form/Step4CustodyRelationship';
import Step5Finances from '../components/form/Step5Finances';
import Step6OtherParties from '../components/form/Step6OtherParties';
import Step7Court from '../components/form/Step7Court';
import Step8LegalDetails from '../components/form/Step8LegalDetails';
import Step9SpecificMatters from '../components/form/Step9SpecificMatters';
import Step10Declaration from '../components/form/Step10Declaration';
import PrintableForm from '../components/form/PrintableForm';
import CertificationPanel from '../components/form/CertificationPanel';
import SavePDFDialog from '../components/form/SavePDFDialog';


const STEP_COMPONENTS = [
  Step1PersonalDetails, Step2LanguageHealth, Step3Employment, Step4CustodyRelationship,
  Step5Finances, Step6OtherParties, Step7Court, Step8LegalDetails,
  Step9SpecificMatters, Step10Declaration,
];

export default function LegalAidForm({ isPortalMode, brandType = "lacw" }) {
  const brandKey = brandType ? `${brandType}_` : "";
  const vlaCompletedKey = `${brandKey}vla_completed_list`;
  const vlaDraftsKey = `${brandKey}vla_drafts_list`;
  const vlaActiveDraftIdKey = `${brandKey}vla_active_draft_id`;
  const vlaDraftKeyPrefix = `${brandKey}vla_draft_`;
  const vlaPrefillKey = `${brandKey}vla_prefill_pending`;
  const demographicsKey = `${brandKey}active_client_demographics`;
  const guidelinesCompletedKey = `${brandKey}guidelines_completed_list`;

  const updateActiveDemographics = (currentData) => {
    try {
      const client = {
        first_name: currentData.first_name || "",
        surname: currentData.last_name || "",
        dob: currentData.dob || "",
        file_no: currentData.file_number || currentData.file_no || "",
      };
      clientStorage.setItem(demographicsKey, JSON.stringify(client));
    } catch (e) {
      console.error("Failed to update active client demographics in VLA form:", e);
    }
  };

  const [currentStep, setCurrentStep] = useState(0);
  /** @type {any} */
  const [formData, setFormData] = useState({ first_name: '', last_name: '' });
  const [applicationId, setApplicationId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [backUrl, setBackUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const printRef = React.useRef(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareRecipientEmail, setShareRecipientEmail] = useState("");
  const [sendingShareEmail, setSendingShareEmail] = useState(false);
  const [showDraftsMenu, setShowDraftsMenu] = useState(false);
  const draftsMenuRef = React.useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const back = params.get('back');
    if (back) {
      setBackUrl(getSafeBackUrl(back));
    }
  }, []);

  // Load existing draft from URL param
  // Load existing draft from URL param or active clientStorage draft
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      loadApplication(id);
    } else {
      setLoaded(true);
    }

    const pending = clientStorage.getItem(vlaPrefillKey);
    if (pending === 'true') {
      clientStorage.removeItem(vlaPrefillKey);
      // Delay slightly to ensure form is loaded
      setTimeout(() => {
        prefillFromCertification();
      }, 150);
    }
  }, [vlaPrefillKey]);


  const loadApplication = async (id) => {
    // 1. Try local storage first
    const localData = clientStorage.getItem(`${vlaDraftKeyPrefix}${id}`);
    if (localData) {
      try {
        const app = JSON.parse(localData);
        setFormData(app.form_data || { first_name: '', last_name: '' });
        setCurrentStep(app.current_step || 0);
        setCompletedSteps(app.completed_steps || []);
        setApplicationId(id);
        if (app.status === 'submitted') setSubmitted(true);
        
        // Ensure the URL has the correct ID
        const params = new URLSearchParams(window.location.search);
        if (params.get('id') !== id) {
          params.set('id', id);
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        }
        setLoaded(true);
        return;
      } catch (err) {
        console.error("Failed to parse local draft:", err);
      }
    }

    // 2. Fallback to cloud db for legacy links
    try {
      const app = await base44.entities.LegalAidApplication.get(id);
      if (app) {
        setFormData(app.form_data || { first_name: '', last_name: '' });
        setCurrentStep(app.current_step || 0);
        setApplicationId(app.id);
        if (app.status === 'submitted') setSubmitted(true);
      }
    } catch (err) {
      console.error("Failed to fetch cloud app:", err);
    }
    setLoaded(true);
  };

  // Autosave to clientStorage on any state change
  useEffect(() => {
    if (!loaded || submitted) return;

    // Check if the form actually has data to avoid saving blank initial states
    const hasData = Object.values(formData).some(
      val => val !== '' && val !== null && val !== undefined
    );
    if (!hasData) return;

    let currentId = applicationId;
    if (!currentId) {
      currentId = `draft_${crypto.randomUUID()}`;
      setApplicationId(currentId);
      
      const params = new URLSearchParams(window.location.search);
      params.set('id', currentId);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }

    const payload = {
      id: currentId,
      form_data: formData,
      current_step: currentStep,
      completed_steps: completedSteps,
      applicant_name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
      status: 'draft',
      updated_at: new Date().toISOString(),
    };

    clientStorage.setItem(`${vlaDraftKeyPrefix}${currentId}`, JSON.stringify(payload));
    clientStorage.setItem(vlaActiveDraftIdKey, currentId);
    updateActiveDemographics(formData);
    updateDraftsList(currentId, payload.applicant_name);
  }, [formData, currentStep, completedSteps, applicationId, submitted, loaded, vlaActiveDraftIdKey, vlaDraftKeyPrefix]);

  const handleChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const currentId = applicationId || `draft_${crypto.randomUUID()}`;
    const payload = {
      id: currentId,
      form_data: formData,
      current_step: currentStep,
      completed_steps: completedSteps,
      applicant_name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
      status: 'draft',
      updated_at: new Date().toISOString(),
    };

    clientStorage.setItem(`${vlaDraftKeyPrefix}${currentId}`, JSON.stringify(payload));
    clientStorage.setItem(vlaActiveDraftIdKey, currentId);
    updateActiveDemographics(formData);
    updateDraftsList(currentId, payload.applicant_name);
    setApplicationId(currentId);

    const params = new URLSearchParams(window.location.search);
    params.set('id', currentId);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);

    setSaving(false);
    toast.success("Draft saved locally in browser!");
  };

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [saveCopyLocally, setSaveCopyLocally] = useState(true);

  // Where certification is switched on for the firm, the application can't leave the portal
  // (emailed, printed or downloaded) until a certifier has certified these exact answers.
  // CertificationPanel works that out and reports it here.
  const [lodging, setLodging] = useState({ state: "checking", canLodge: false, message: "Checking certification…" });
  const holdUntilCertified = () => {
    if (lodging.canLodge) return false;
    toast.error(lodging.message || "This application must be certified before it can be lodged.");
    document.getElementById("certification-status")?.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  };

  const ensureApplicationId = () => {
    let appId = applicationId;
    if (!appId) {
      appId = `draft_${crypto.randomUUID()}`;
      setApplicationId(appId);
      
      const payload = {
        id: appId,
        form_data: formData,
        current_step: currentStep,
        completed_steps: completedSteps,
        applicant_name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
        status: 'draft',
        updated_at: new Date().toISOString(),
      };
      clientStorage.setItem(`${vlaDraftKeyPrefix}${appId}`, JSON.stringify(payload));
      clientStorage.setItem(vlaActiveDraftIdKey, appId);
      updateActiveDemographics(formData);

      const params = new URLSearchParams(window.location.search);
      params.set('id', appId);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
    return appId;
  };

  const handleEmailFormClick = () => {
    if (holdUntilCertified()) return;
    ensureApplicationId();
    setShowEmailModal(true);
  };

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    if (!recipientEmail) return;
    const success = await handleSendEmail({
      recipientEmail,
      ccEmail,
      message: emailMessage,
      saveCopy: saveCopyLocally
    });
    if (success) {
      setShowEmailModal(false);
      setRecipientEmail("");
      setCcEmail("");
      setEmailMessage("");
    }
  };

  const attachmentsList = [];
  if (formData.centrelink_card_attachment) {
    attachmentsList.push({
      label: "Client's Centrelink Card",
      name: formData.centrelink_card_attachment_name || "centrelink_card.pdf"
    });
  }
  if (formData.partner_centrelink_card_attachment) {
    attachmentsList.push({
      label: "Partner's Centrelink Card",
      name: formData.partner_centrelink_card_attachment_name || "partner_centrelink_card.pdf"
    });
  }
  if (formData.fees_proof_attachment) {
    attachmentsList.push({
      label: "Proof of Payment of Legal Fees",
      name: formData.fees_proof_attachment_name || "fees_proof.pdf"
    });
  }
  if (formData.priors_attachment) {
    attachmentsList.push({
      label: "Prior Convictions Record",
      name: formData.priors_attachment_name || "priors.pdf"
    });
  }
  const declarationFileName = `Telephone_Declaration_${`${formData.first_name || 'VLA'}_${formData.last_name || 'Application'}`.replace(/[^a-z0-9]/gi, '_')}.pdf`;
  if (formData.declaration_method === "telephone_declaration") {
    attachmentsList.push({
      label: "Telephone declaration record",
      name: declarationFileName
    });
  }

  /** @param {Uint8Array} bytes */
  const bytesToDataUrl = (bytes) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(new Blob([bytes], { type: 'application/pdf' }));
  });

  const handleSendEmail = async ({ recipientEmail, ccEmail, message, saveCopy = true }) => {
    setEmailing(true);
    try {
      const appId = applicationId || `draft_${crypto.randomUUID()}`;
      
      // 1. Generate PDF booklet
      let pdfBase64 = null;
      let pdfBytes = null;
      try {
        pdfBytes = await generateVLAPdf(formData);
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(new Blob([pdfBytes], { type: 'application/pdf' }));
        });
        const fullBase64 = await base64Promise;
        pdfBase64 = fullBase64.split(',')[1];
      } catch (err) {
        console.error("Failed to generate PDF for email:", err);
      }

      // Check if remote email mode
      const INTEGRATION_MODE = (typeof import.meta !== "undefined" && import.meta.env ? import.meta.env.VITE_APP_INTEGRATION_MODE : "") || "standalone";
      const API_BASE_URL = (typeof import.meta !== "undefined" && import.meta.env ? import.meta.env.VITE_APP_API_BASE_URL : "") || "";
      const isRemote = INTEGRATION_MODE === "remote" && Boolean(API_BASE_URL);

      const safeName = `${formData.first_name || 'VLA'}_${formData.last_name || 'Application'}`.replace(/[^a-z0-9]/gi, '_');

      if (!isRemote) {
        // Automatically download the PDF to PC for attachment
        if (pdfBytes) {
          try {
            /** @type {any} */
            const pdfData = pdfBytes;
            const blob = new Blob([pdfData], { type: 'application/pdf' });
            await savePDFFile(blob, `VLA_Application_${safeName}.pdf`);
          } catch (dlErr) {
            console.error("Failed to save local copy for mailto attachment:", dlErr);
          }
        }

        if (formData.declaration_method === "telephone_declaration") {
          try {
            const declarationBytes = await createTelephoneDeclarationPdf(formData, brandType);
            await savePDFFile(new Blob([declarationBytes], { type: 'application/pdf' }), declarationFileName);
          } catch (declErr) {
            console.error("Failed to save telephone declaration for mailto attachment:", declErr);
          }
        }

        // Open native mail app
        const clientName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();
        const subject = `Legal Aid Application${clientName ? ` - ${clientName}` : ''}`;
        const standaloneMailBody = `${message || ''}\n\n[Please attach the downloaded VLA_Application_${safeName}.pdf file here]`;
        
        // Write to standalone outbox database so it lands in the Submissions Inbox!
        try {
          await base44.integrations.Core.SendEmail({
            to: recipientEmail,
            cc: ccEmail || "",
            subject: subject,
            body: standaloneMailBody,
            attachment_name: `VLA_Application_${safeName}.pdf`,
            attachment_type: "application/pdf",
            attachment_base64: pdfBase64
          });
        } catch (emailErr) {
          console.warn("Failed to write standalone outbox:", emailErr);
        }

        const mailtoUrl = `mailto:${encodeURIComponent(recipientEmail)}?subject=${encodeURIComponent(subject)}${ccEmail ? `&cc=${encodeURIComponent(ccEmail)}` : ''}&body=${encodeURIComponent(standaloneMailBody)}`;
        window.location.href = mailtoUrl;

        toast.success("Opened email client! The PDF has been downloaded for attachment.");
        if (applicationId) {
          saveVlaToCompletedList(applicationId, formData);
          deleteFromDraftsList(applicationId);
          setApplicationId(null);
          window.history.replaceState({}, '', window.location.pathname);
        }
        return true;
      }

      // 2. Gather attachments (remote mode only)
      const attachments = [];
      if (formData.centrelink_card_attachment) {
        attachments.push({
          name: formData.centrelink_card_attachment_name || 'centrelink_card.pdf',
          url: formData.centrelink_card_attachment
        });
      }
      if (formData.partner_centrelink_card_attachment) {
        attachments.push({
          name: formData.partner_centrelink_card_attachment_name || 'partner_centrelink_card.pdf',
          url: formData.partner_centrelink_card_attachment
        });
      }
      if (formData.fees_proof_attachment) {
        attachments.push({
          name: formData.fees_proof_attachment_name || 'fees_proof.pdf',
          url: formData.fees_proof_attachment
        });
      }
      if (formData.priors_attachment) {
        attachments.push({
          name: formData.priors_attachment_name || 'priors.pdf',
          url: formData.priors_attachment
        });
      }

      // A telephone declaration is part of the application, so it goes with it. If it
      // can't be made, stop rather than send the application without it.
      if (formData.declaration_method === "telephone_declaration") {
        let declarationBytes;
        try {
          declarationBytes = await createTelephoneDeclarationPdf(formData, brandType);
        } catch (err) {
          console.error("Failed to create telephone declaration PDF:", err);
          toast.error("The telephone declaration couldn't be attached, so the application wasn't sent. Please try again.");
          return false;
        }
        attachments.push({ name: declarationFileName, url: await bytesToDataUrl(declarationBytes) });
      }

      await base44.functions.invoke('submitApplicationEmail', {
        formData,
        applicationId: appId,
        recipientEmail,
        ccEmail,
        message,
        pdfBase64,
        attachments
      });

      // 3. Save copy locally on PC (remote mode only, if requested)
      if (pdfBytes && saveCopy) {
        try {
          /** @type {any} */
          const pdfData = pdfBytes;
          const blob = new Blob([pdfData], { type: 'application/pdf' });
          await savePDFFile(blob, `VLA_Application_${safeName}.pdf`);
        } catch (dlErr) {
          console.error("Failed to save local copy after email:", dlErr);
        }
      }

      toast.success("Application emailed successfully!");
      if (applicationId) {
        saveVlaToCompletedList(applicationId, formData);
        deleteFromDraftsList(applicationId);
        setApplicationId(null);
        window.history.replaceState({}, '', window.location.pathname);
      }
      return true;
    } catch (err) {
      console.error("Email failed:", err);
      toast.error("Failed to send email");
      return false;
    } finally {
      setEmailing(false);
    }
  };


  const [showPrintMenu, setShowPrintMenu] = React.useState(false);
  const printMenuRef = React.useRef(null);
  
  // Safe Unicode Base64 Encoding
  const encodeDraft = (data) => {
    try {
      const jsonStr = JSON.stringify(data);
      const bytes = new TextEncoder().encode(jsonStr);
      const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join("");
      return btoa(binString);
    } catch (e) {
      console.error(e);
      return "";
    }
  };

  const getShareUrl = () => {
    if (!formData) return "";
    const encoded = encodeDraft(formData);
    
    // Determine the base origin
    let baseOrigin = "";
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (
        hostname === "localhost" || 
        hostname === "127.0.0.1" || 
        hostname.includes("gitpod") || 
        hostname.includes("vercel.app")
      ) {
        baseOrigin = window.location.origin;
      } else {
        baseOrigin = "https://portal.completelawsupport.com";
      }
    }
    
    const pathname = `/${brandType}-aidapplication`;
    
    try {
      const url = new URL(pathname, baseOrigin);
      url.searchParams.set("draft_data", encoded);
      return url.toString();
    } catch (e) {
      console.error("Failed to construct share URL:", e);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("draft_data", encoded);
        return url.toString();
      } catch (err) {
        return "";
      }
    }
  };

  const handleExportDraftFile = () => {
    try {
      if (!formData) {
        toast.error("No form data to export.");
        return;
      }
      
      const clientName = `${formData.first_name || ""} ${formData.last_name || ""}`.trim() || "Unnamed Client";

      const payload = {
        client_name: clientName,
        form_name: "legal_aid",
        brand: brandType,
        updated_at: new Date().toISOString(),
        form_data: formData
      };
      
      const dataStr = JSON.stringify(payload, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      
      const clientNameSafe = clientName.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const exportFileDefaultName = `${brandType}_legal_aid_draft_${clientNameSafe}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      toast.success("Draft exported successfully!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to export draft.");
    }
  };

  const handleImportDraftFile = (e) => {
    const fileReader = new FileReader();
    const file = e.target.files[0];
    if (!file) return;

    fileReader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!imported.form_data && !imported) {
          toast.error("Invalid draft file format. Could not load.");
          return;
        }
        setFormData(imported.form_data || imported);
        toast.success("Successfully imported draft!");
        e.target.value = "";
      } catch (err) {
        console.error(err);
        toast.error("Failed to parse draft file. Make sure it is a valid JSON draft.");
      }
    };
    fileReader.readAsText(file);
  };

  const handleSendDraftEmail = async (e) => {
    e.preventDefault();
    if (!shareRecipientEmail) {
      toast.error("Please enter a recipient email address.");
      return;
    }

    setSendingShareEmail(true);
    try {
      const url = getShareUrl();
      const clientName = `${formData.first_name || ""} ${formData.last_name || ""}`.trim() || "Client";
      
      const emailSubject = `Shared Draft: Legal Aid Application for ${clientName}`;
      const emailBody = `
Hi,

A draft for ${clientName} has been shared with you on Complete Law Support.

Click the link below to open the form with the draft data prefilled:
${url}

Regards,
Complete Law Support Portal
      `.trim();

      const response = await fetch("/api/send-draft-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: shareRecipientEmail,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to send email");
      }

      toast.success(`Draft link sent successfully to ${shareRecipientEmail}!`);
      setShowShareModal(false);
      setShareRecipientEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send draft link email.");
    } finally {
      setSendingShareEmail(false);
    }
  };

  // Helper to get all saved drafts
  const getSavedDrafts = () => {
    try {
      const listStr = clientStorage.getItem(vlaDraftsKey);
      return listStr ? JSON.parse(listStr) : [];
    } catch {
      return [];
    }
  };

  const saveVlaToCompletedList = (id, currentData) => {
    try {
      const completedInfo = {
        id: id || `vla-completed-${crypto.randomUUID()}`,
        applicant_name: `${currentData.first_name || ''} ${currentData.last_name || ''}`.trim() || 'Unnamed Client',
        completed_at: new Date().toISOString(),
        form_data: currentData,
        type: 'VLA'
      };
      const existingStr = clientStorage.getItem(vlaCompletedKey);
      let list = [];
      if (existingStr) {
        try { list = JSON.parse(existingStr); } catch (e) {}
      }
      list = list.filter(item => item.applicant_name !== completedInfo.applicant_name);
      list.push(completedInfo);
      clientStorage.setItem(vlaCompletedKey, JSON.stringify(list));
      updateActiveDemographics(currentData);
      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error("Failed to save completed VLA to clientStorage:", e);
    }
  };

  // Helper to save a draft to the list
  const updateDraftsList = (id, applicantName) => {
    try {
      const list = getSavedDrafts();
      const existingIdx = list.findIndex(d => d.id === id);
      const draftInfo = {
        id,
        applicant_name: applicantName || 'Unnamed Client',
        updated_at: new Date().toISOString(),
        current_step: currentStep
      };
      if (existingIdx > -1) {
        list[existingIdx] = draftInfo;
      } else {
        list.push(draftInfo);
      }
      clientStorage.setItem(vlaDraftsKey, JSON.stringify(list));
    } catch (e) {
      console.error("Failed to update drafts list:", e);
    }
  };

  const deleteFromDraftsList = (id) => {
    try {
      const list = getSavedDrafts();
      const filtered = list.filter(d => d.id !== id);
      clientStorage.setItem(vlaDraftsKey, JSON.stringify(filtered));
      clientStorage.removeItem(`${vlaDraftKeyPrefix}${id}`);
      if (clientStorage.getItem(vlaActiveDraftIdKey) === id) {
        clientStorage.removeItem(vlaActiveDraftIdKey);
      }
    } catch (e) {
      console.error("Failed to delete from drafts list:", e);
    }
  };

  // Close print and drafts menus on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target)) {
        setShowPrintMenu(false);
      }
      if (draftsMenuRef.current && !draftsMenuRef.current.contains(e.target)) {
        setShowDraftsMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // When the client declared by telephone, the populated VLA declaration record is part of
  // the application - not a separate errand to remember. Both the print and the download
  // path call this, so they cannot drift apart.
  //
  // It opens as its own document rather than being merged into the VLA PDF: the record has
  // its own page rules and its layout is the one VLA expects to see, and rebuilding it to
  // merge would change how it looks.
  const printTelephoneDeclarationIfSelected = () => {
    if (formData.declaration_method !== "telephone_declaration") return;
    try {
      printTelephoneDeclaration(formData, brandType);
    } catch (error) {
      // The application itself has already printed or saved by this point, so this must
      // not read as though that failed.
      toast.error(
        error?.message
          || "The application is ready, but the telephone declaration could not be opened. Allow pop-ups for this site and try again.",
      );
    }
  };

  const handlePrint = async () => {
    setShowPrintMenu(false);
    if (holdUntilCertified()) return;
    setDownloadingPDF(true);
    try {
      const pdfBytes = await generateVLAPdf(formData);
      /** @type {any} */
      const pdfData = pdfBytes;
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      // Create an invisible iframe to seamlessly trigger the print dialog for the generated PDF
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 100);
      };
      toast.success("Preparing PDF for print...");
      printTelephoneDeclarationIfSelected();
      if (applicationId) {
        saveVlaToCompletedList(applicationId, formData);
        deleteFromDraftsList(applicationId);
        setApplicationId(null);
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare PDF for printing");
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handlePrintDownload = async () => {
    console.log("🖱️ 'Download PDF' button clicked in menu.");
    await handleDownloadPDF();
  };



  const generatePDF = async () => {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    const element = printRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const doc = new jsPDF('p', 'mm', 'a4');

    let heightLeft = imgHeight;
    let position = 0;
    const imgData = canvas.toDataURL('image/png');

    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= 297; // A4 height in mm

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= 297;
    }

    doc.save(`legal_aid_application_${applicationId || 'draft'}.pdf`);
  };

  const handleDownloadPDF = async () => {
    console.log("🖱️ Download button clicked.");
    if (holdUntilCertified()) return;
    setDownloadingPDF(true);
    try {
      console.log("🔗 Attempting to reach PDF backend at http://127.0.0.1:3001...");
      try {
        const response = await fetch('http://127.0.0.1:3001/api/fill-vla', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          const blob = await response.blob();
          const safeName = (formData.first_name || 'Draft').replace(/[^a-z0-9]/gi, '_');
          await savePDFFile(blob, `VLA_Application_${safeName}.pdf`);
          printTelephoneDeclarationIfSelected();
          if (applicationId) {
            saveVlaToCompletedList(applicationId, formData);
            deleteFromDraftsList(applicationId);
            setApplicationId(null);
            window.history.replaceState({}, '', window.location.pathname);
          }
          return;
        }
      } catch (backendErr) {
        console.error("❌ Could not reach backend:", backendErr);
      }

      // Fallback
      console.log("🔄 Running Browser Fallback...");
      const pdfBytes = await generateVLAPdf(formData);
      /** @type {any} */
      const pdfData = pdfBytes;
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      await savePDFFile(blob, `VLA_Application_Fallback.pdf`);
      if (applicationId) {
        saveVlaToCompletedList(applicationId, formData);
        deleteFromDraftsList(applicationId);
        setApplicationId(null);
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (err) {
      console.error("💥 Error:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloadingPDF(false);
      if (typeof setShowPDFDialog === 'function') setShowPDFDialog(false);
    }
  };

  const proceedWithSubmit = async () => {
    setSaving(true);
    let appId = applicationId || `draft_${crypto.randomUUID()}`;
    const payload = {
      id: appId,
      form_data: formData,
      current_step: currentStep,
      completed_steps: completedSteps,
      applicant_name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
      status: 'submitted',
      submission_date: new Date().toISOString(),
    };

    try {
      // Save locally
      clientStorage.setItem(`${vlaDraftKeyPrefix}${appId}`, JSON.stringify(payload));
      saveVlaToCompletedList(appId, formData);
      deleteFromDraftsList(appId);
      setApplicationId(appId);

      // Send email with PDF attachment
      await base44.functions.invoke('submitApplicationEmail', {
        formData,
        applicationId: appId,
      });

      setSaving(false);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      setSaving(false);
      console.error('Submission failed:', err);
      toast.error(`Submission failed: ${err.message}`);
    }
  };

  const handleSubmit = async () => {
    setShowPDFDialog(true);
  };

  const goNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goPrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prefillFromCertification = () => {
    try {
      let activeClientStr = null;
      
      // Primary: load from completed guideline certification
      const certsStr = clientStorage.getItem(guidelinesCompletedKey);
      if (certsStr) {
        try {
          const certs = JSON.parse(certsStr);
          if (certs.length > 0) {
            const latestCert = certs[certs.length - 1];
            const parts = (latestCert.client_name || "").trim().split(/\s+/);
            const demographics = {
              first_name: parts[0] || "",
              surname: parts.slice(1).join(" ") || "",
              dob: latestCert.form_data?.client_dob || "",
              file_no: latestCert.form_data?.file_ref || "",
              lawyer: latestCert.form_data?.lawyer || ""
            };
            activeClientStr = JSON.stringify(demographics);
          }
        } catch (e) {
          console.error("Failed to parse guidelines list:", e);
        }
      }

      // Fallback: load from active client demographics (intake)
      if (!activeClientStr) {
        activeClientStr = clientStorage.getItem(demographicsKey);
      }

      if (!activeClientStr) {
        toast.error("No saved certification sheet or active intake details found.");
        return;
      }
      const client = JSON.parse(activeClientStr);
      
      const firstName = client.first_name || "";
      const lastName = client.surname || "";
      const dob = client.dob || "";
      const gender = client.gender || "";
      const email = client.email || "";
      const phone = client.phone || "";
      const resAddress = client.res_address || "";
      const resSuburb = client.res_suburb || "";
      const resPostcode = client.res_postcode || "";
      const financialCrn = client.financial_crn || "";
      const indigenousIdentity = client.indigenous_identity || "";
      const centrelinkBenefit = client.centrelink_benefit || "";
      const inCustody = client.in_custody || "";

      // Compile address
      const addressParts = [];
      if (resAddress) addressParts.push(resAddress);
      if (resSuburb) addressParts.push(resSuburb);
      if (resPostcode) addressParts.push(`VIC ${resPostcode}`);
      const compiledAddress = addressParts.join(", ");

      // Map indigenous identity
      let atsiOrigin = "";
      if (indigenousIdentity === "Aboriginal") atsiOrigin = "Yes, Aboriginal";
      else if (indigenousIdentity === "Torres Strait Islander") atsiOrigin = "Yes, Torres Strait Islander";
      else if (indigenousIdentity === "Both") atsiOrigin = "Yes, Aboriginal and Torres Strait Islander";
      else if (indigenousIdentity === "No") atsiOrigin = "No";

      // Map benefit types
      const benefitMap = {
        "abstudy": "Abstudy",
        "age pension": "Age pension",
        "austudy": "Austudy",
        "carer payment": "Carer payment",
        "disability support pension": "Disability support pension",
        "jobseeker payment": "JobSeeker",
        "jobseeker": "JobSeeker",
        "parenting payment": "Parenting payment",
        "youth allowance": "Youth allowance",
        "partner allowance": "Partner allowance",
        "sickness allowance": "Sickness allowance",
        "special benefit": "Special benefits",
        "veterans war service pension": "Veterans/war service",
        "widow allowance": "Widow allowance",
        "widow b pension": "Widow B pension",
        "wife pension": "Wife pension",
        "other": "Other"
      };
      
      let mappedBenefit = "";
      if (centrelinkBenefit) {
        const lowerBenefit = centrelinkBenefit.toLowerCase();
        mappedBenefit = benefitMap[lowerBenefit] || "";
        if (!mappedBenefit) {
          const matchKey = Object.keys(benefitMap).find(k => lowerBenefit.includes(k) || k.includes(lowerBenefit));
          if (matchKey) {
            mappedBenefit = benefitMap[matchKey];
          }
        }
      }

      setFormData(prev => {
        const updated = {
          ...prev,
          first_name: firstName || prev.first_name,
          last_name: lastName || prev.last_name,
          date_of_birth: dob || prev.date_of_birth,
          gender: gender || prev.gender,
          email: email || prev.email,
          mobile_phone: phone || prev.mobile_phone,
          home_address: compiledAddress || prev.home_address,
          currently_in_custody: inCustody || prev.currently_in_custody,
          contact_email_pref: email ? "Yes" : prev.contact_email_pref,
          declaration_file_reference: client.file_no || prev.declaration_file_reference || "",
        };

        if (atsiOrigin) {
          updated.atsi_origin = atsiOrigin;
        }

        if (financialCrn || mappedBenefit) {
          updated.receives_benefit = "Yes";
          if (financialCrn) {
            updated.centrelink_ref = financialCrn;
          }
          if (mappedBenefit) {
            const currentTypes = Array.isArray(prev.benefit_types) ? prev.benefit_types : [];
            if (!currentTypes.includes(mappedBenefit)) {
              updated.benefit_types = [...currentTypes, mappedBenefit];
            }
          }
        }

        return updated;
      });

      if (firstName || lastName) {
        toast.success(`Prefilled details for ${firstName || "Client"} from Certification Form / Intake.`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleClearForm = () => {
    if (window.confirm('Are you sure you want to clear the entire form? This cannot be undone.')) {
      if (applicationId) {
        clientStorage.removeItem(`${vlaDraftKeyPrefix}${applicationId}`);
      }
      clientStorage.removeItem(vlaActiveDraftIdKey);
      setFormData({ first_name: '', last_name: '' });
      setCurrentStep(0);
      setCompletedSteps([]);
      setApplicationId(null);
      
      // Clear URL query parameters
      window.history.replaceState({}, '', window.location.pathname);
      toast.success("Form cleared");
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-10 text-center"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Application Submitted</h2>
            <p className="text-gray-500 leading-relaxed mb-6">
              Your demo application has been submitted successfully. This demo keeps the workflow local and does not send a live application.
            </p>
            <p className="text-sm text-gray-400">Reference ID: {applicationId}</p>
          </motion.div>
        </div>
      </div>
    );
  }

  /** @type {any} */
  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <div id="print-area" className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30" ref={printRef}>
      <style>{`
          .vla-print-document {
            display: block;
          }
          
          @media print {
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { background: white; font-family: Arial, sans-serif; }
            #print-area > div:not(.vla-print-document) { display: none !important; }
            .vla-print-document { display: block !important; position: static !important; left: auto !important; }
            
            .vla-print-header {
              background-color: #007AB9;
              color: white;
              padding: 20px;
              margin: 0 0 20px 0;
              page-break-after: avoid;
            }
            .vla-print-header h1 { font-size: 20px; font-weight: 700; margin: 10px 0 4px 0; color: white; }
            .vla-print-header p { font-size: 11px; margin: 0; color: #e0f2fe; }
            .vla-print-header-meta {
              display: flex;
              justify-content: space-between;
              gap: 16px;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #e0f2fe;
            }

            .vla-section-header {
              background-color: #007AB9;
              color: white;
              padding: 6px 12px;
              margin: 20px 0 12px 0;
              font-weight: bold;
              font-size: 12px;
              page-break-after: avoid;
            }

            .vla-form-content {
              padding: 0 20px;
              font-size: 11px;
              line-height: 1.5;
              color: #333;
            }

            .vla-form-row {
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 20px;
              margin-bottom: 12px;
              page-break-inside: avoid;
            }

            .vla-form-field {
              display: flex;
              flex-direction: column;
            }

            .vla-form-field label {
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 2px;
              color: #333;
            }

            .vla-form-field div {
              border-bottom: 1px dotted #333;
              padding: 3px 0;
              min-height: 14px;
              font-size: 11px;
            }

            .vla-form-field-full {
              grid-column: 1 / -1;
              margin-bottom: 12px;
            }

            .vla-paragraph-block {
              margin-bottom: 14px;
              page-break-inside: avoid;
            }

            .vla-paragraph-block label {
              display: block;
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 4px;
              color: #333;
            }

            .vla-paragraph-block div {
              border: 1px solid #d1d5db;
              padding: 8px 10px;
              min-height: 34px;
              white-space: pre-wrap;
            }

            .vla-table-wrap,
            .vla-subrecord,
            .vla-signature-block {
              margin-bottom: 14px;
              page-break-inside: avoid;
            }

            .vla-table-title {
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 6px;
              color: #333;
            }

            .vla-data-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }

            .vla-data-table th,
            .vla-data-table td {
              border: 1px solid #d1d5db;
              padding: 5px 6px;
              text-align: left;
              vertical-align: top;
            }

            .vla-data-table th {
              background: #f3f4f6;
              font-weight: bold;
            }

            .vla-signature-image-wrap {
              border: 1px solid #d1d5db;
              padding: 8px;
              min-height: 60px;
              display: flex;
              align-items: center;
            }

            .vla-signature-image {
              max-height: 54px;
              max-width: 180px;
              object-fit: contain;
            }

            .vla-signature-date {
              font-size: 10px;
              margin-top: 4px;
              color: #4b5563;
            }

            .vla-print-footer {
              margin: 24px 20px 0 20px;
              padding-top: 10px;
              border-top: 1px solid #d1d5db;
              font-size: 10px;
              text-align: center;
              color: #666;
            }

            .vla-checkbox-group {
              display: flex;
              gap: 15px;
              flex-wrap: wrap;
              margin: 6px 0;
            }

            .vla-checkbox {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 10px;
            }

            .vla-checkbox input {
              width: 11px;
              height: 11px;
              margin: 0;
            }
          }
        `}</style>
      <PrintableForm formData={formData} applicationId={applicationId} brandType={brandType} />
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 print-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4 min-w-0">
              {backUrl && !isPortalMode && (
                <a
                  href={backUrl}
                  target={window.top !== window.self ? "_top" : "_self"}
                  onClick={() => {
                    if (window.self === window.top) {
                      try {
                        window.close();
                      } catch (err) {}
                    }
                  }}
                  className="flex items-center gap-1.5 text-gray-500 hover:text-[#0071BC] transition-colors text-sm font-medium mr-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Portal</span>
                </a>
              )}
              {!isPortalMode && (
                <div className="w-10 h-10 bg-[#0071BC] rounded-xl flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold text-gray-900 truncate">Legal Assistance Application</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="relative" ref={draftsMenuRef}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDraftsMenu(prev => !prev)}
                  className="gap-1.5 text-slate-700 border-slate-200"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  Drafts & Share
                  <ChevronDown className="w-3.5 h-3.5 ml-0.5" />
                </Button>
                {showDraftsMenu && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-96 overflow-y-auto">
                    {/* Draft Actions */}
                    <div className="p-1 border-b border-gray-100 space-y-0.5">
                      <button
                        onClick={() => {
                          handleSave();
                          setShowDraftsMenu(false);
                        }}
                        disabled={saving}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors"
                      >
                        <Save className="w-3.5 h-3.5 text-slate-400" />
                        <span>Save Local Draft</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowShareModal(true);
                          setShowDraftsMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors"
                      >
                        <Share2 className="w-3.5 h-3.5 text-slate-400" />
                        <span>Share Draft Link</span>
                      </button>

                    </div>

                    {/* Local Drafts History */}
                    {brandType === 'lacw' && (
                      <>
                        <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                          Local Drafts History
                        </div>
                        {getSavedDrafts().length === 0 ? (
                          <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                            No saved local drafts
                          </div>
                        ) : (
                          <div className="p-1 max-h-48 overflow-y-auto divide-y divide-gray-50">
                            {getSavedDrafts().map(draft => (
                              <button
                                key={draft.id}
                                onClick={() => {
                                  loadApplication(draft.id);
                                  setShowDraftsMenu(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors border-b border-gray-50 last:border-0"
                              >
                                <div className="font-semibold text-xs text-gray-800 truncate">
                                  {draft.applicant_name || "Unnamed Client"}
                                </div>
                                <div className="text-[10px] text-gray-400 flex justify-between mt-0.5 font-medium">
                                  <span>Step {draft.current_step + 1}</span>
                                  <span>{new Date(draft.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Reset Form Option */}
                    <div className="p-1 bg-slate-50 border-t border-gray-100">
                      <button
                        onClick={() => {
                          handleClearForm();
                          setShowDraftsMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-red-400" />
                        <span>Reset Form</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Button
                size="sm"
                onClick={handleEmailFormClick}
                disabled={emailing}
                aria-disabled={!lodging.canLodge}
                title={lodging.canLodge ? undefined : lodging.message}
                className={`gap-2 bg-[#0071BC] hover:bg-[#005A96] ${lodging.canLodge ? "" : "opacity-50"}`}
              >
                <Mail className="w-4 h-4" />
                {emailing ? 'Sending...' : 'Email Application'}
              </Button>

              <div className="relative" ref={printMenuRef}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { if (!holdUntilCertified()) setShowPrintMenu(prev => !prev); }}
                  aria-disabled={!lodging.canLodge}
                  title={lodging.canLodge ? undefined : lodging.message}
                  className={`gap-2 text-slate-700 border-slate-200 ${lodging.canLodge ? "" : "opacity-50"}`}
                >
                  <Printer className="w-4 h-4" />
                  Print / Download
                  <ChevronDown className="w-3.5 h-3.5 ml-0.5" />
                </Button>
                {showPrintMenu && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    <button
                      onClick={handlePrint}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Printer className="w-4 h-4 text-gray-400" />
                      <div className="text-left">
                        <div className="font-medium">Print / Save as PDF</div>
                        <div className="text-xs text-gray-400">Uses browser print dialog</div>
                      </div>
                    </button>
                    <div className="border-t border-gray-100" />
                    <button
                      onClick={handlePrintDownload}
                      disabled={downloadingPDF}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <Download className="w-4 h-4 text-gray-400" />
                      <div className="text-left">
                        <div className="font-medium">{downloadingPDF ? 'Generating...' : 'Download PDF'}</div>
                        <div className="text-xs text-gray-400">Saves directly to device</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* The filled VLA application is a funding document: it belongs on the
                  matter in Actionstep, not only on the practitioner's device. No file
                  note - the form itself is the record. */}
              <ActionstepSavePanel
                formLabel="Legal Aid Application"
                disabled={!formData.first_name && !formData.last_name}
                saveNote={false}
                getDocuments={async () => {
                  const bytes = await generateVLAPdf(formData);
                  const name = portalDocumentName(
                    "Legal-Aid-Application",
                    [formData.first_name, formData.last_name].filter(Boolean).join(" "),
                  );
                  return [
                    {
                      file: new File([bytes], name, { type: "application/pdf" }),
                      folder: actionstepFolderFor("aidApplication"),
                    },
                  ];
                }}
              />
            </div>
          </div>
          <div className="pb-3">
            <CertificationPanel
              formData={formData}
              applicationId={applicationId}
              ensureApplicationId={ensureApplicationId}
              brandType={brandType}
              onLodgingChange={setLodging}
            />
          </div>
          <FormProgress
            currentStep={currentStep}
            onStepClick={setCurrentStep}
            completedSteps={completedSteps}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex justify-end mb-4 print:hidden">
          <CloudDraftsPanel formType="legal_aid_form" formData={formData} onLoadDraft={setFormData} brand={brandType} />
        </div>
        <div className="mb-6">
          <span className="text-xs font-medium text-[#0071BC] tracking-wide uppercase">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <h2 className="text-xl font-bold text-gray-900 mt-1">{STEPS[currentStep].label}</h2>
          <p className="text-sm text-gray-400 mt-0.5">Sections {STEPS[currentStep].sections}</p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8"
          >
            <StepComponent 
              data={formData} 
              onChange={handleChange} 
              brandType={brandType}
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-end mt-8 pb-8 print-hidden">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={goPrev}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </Button>



            {currentStep < STEPS.length - 1 && (
              <Button onClick={goNext} className="gap-2 bg-[#0071BC] hover:bg-[#005A96]">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>



      {/* Save PDF Dialog */}
      <SavePDFDialog
        open={showPDFDialog}
        onOpenChange={setShowPDFDialog}
        onSave={handleDownloadPDF}
        onSkip={() => {
          setShowPDFDialog(false);
          proceedWithSubmit();
        }}
        isLoading={downloadingPDF}
      />

      {/* EMAIL MODAL */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <form onSubmit={onEmailSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#0071BC]" />
                <span>Email Application</span>
              </DialogTitle>
              <DialogDescription>
                Send a copy of the completed legal aid application PDF along with all uploaded form attachments.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">To <span className="text-red-500">*</span></label>
                <Input
                  type="email"
                  required
                  placeholder="recipient@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">CC</label>
                <Input
                  type="email"
                  placeholder="cc@example.com (optional)"
                  value={ccEmail}
                  onChange={(e) => setCcEmail(e.target.value)}
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">Message</label>
                <Textarea
                  placeholder="Add an optional message body..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  disabled={emailing}
                  rows={4}
                />
              </div>
              <div className="flex items-center gap-2 py-1 select-none">
                <input
                  type="checkbox"
                  id="save_copy_locally"
                  checked={saveCopyLocally}
                  onChange={(e) => setSaveCopyLocally(e.target.checked)}
                  className="w-4 h-4 text-[#0071BC] border-gray-300 rounded focus:ring-[#0071BC] cursor-pointer"
                  disabled={emailing}
                />
                <label htmlFor="save_copy_locally" className="text-xs font-semibold text-gray-700 cursor-pointer">
                  Also save a copy of this application to your PC
                </label>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1.5">Attachments to Send</label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-medium text-gray-600 border-b border-slate-200 pb-2">
                    <FileText className="w-4 h-4 text-[#0071BC]" />
                    <span>VLA_Application_Form.pdf (Compiled PDF)</span>
                  </div>
                  {attachmentsList.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No additional uploaded supporting materials found in this application.</p>
                  ) : (
                    <div className="space-y-2">
                      {attachmentsList.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-white border border-slate-100 rounded-lg p-2.5">
                          <div className="flex items-center gap-2 text-gray-700 min-w-0">
                            <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="font-medium truncate">{file.name}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 font-normal shrink-0 ml-2">({file.label})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setShowEmailModal(false)} disabled={emailing}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0071BC] hover:bg-[#005A96]" disabled={emailing || !recipientEmail}>
                {emailing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".json"
        onChange={handleImportDraftFile}
      />

      {/* SHARE DRAFT LINK MODAL */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-lg">
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[#0071BC]" />
                <span>Share Draft Link</span>
              </DialogTitle>
              <DialogDescription>
                Anyone with this link will see your current form data prefilled immediately upon opening.
              </DialogDescription>
            </DialogHeader>

            {/* Copy Link Section */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 uppercase">Draft URL</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  readOnly
                  value={getShareUrl()}
                  className="flex-1 bg-slate-50 font-mono text-xs text-slate-600 select-all"
                />
                <Button
                  onClick={() => {
                    const url = getShareUrl();
                    if (url) {
                      navigator.clipboard.writeText(url);
                      toast.success("Link copied to clipboard!");
                    }
                  }}
                  variant="outline"
                  className="shrink-0 flex items-center gap-1.5 font-bold"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Email Link Form */}
            <form onSubmit={handleSendDraftEmail} className="border-t border-slate-100 pt-4 space-y-3">
              <label className="text-xs font-semibold text-gray-600 uppercase block">Email Link Directly</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    required
                    placeholder="colleague@completelawsupport.com"
                    value={shareRecipientEmail}
                    onChange={(e) => setShareRecipientEmail(e.target.value)}
                    className="pl-9"
                    disabled={sendingShareEmail}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sendingShareEmail || !shareRecipientEmail}
                  className="bg-[#0071BC] hover:bg-[#005A96]"
                >
                  {sendingShareEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Link
                    </>
                  )}
                </Button>
              </div>
            </form>

            <DialogFooter className="border-t border-slate-100 pt-3">
              <Button type="button" variant="outline" onClick={() => setShowShareModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
